import {
  Box,
  Typography,
  CircularProgress,
  type SelectChangeEvent,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { CommentParentType } from '../../../../models/Comment'
import { Comments } from '../../../customComponents/comments/Comments'
import { FormSelect, type SelectOption } from '../../../customComponents/FormSelect'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type PlanOrganizationTask, PlanOrganizationTaskName, PlanOrganizationTaskStatus } from '../../../../models/PlanOrganizationTask'
import { GetAnnualPlanStatus, GetBudgetSummary, GetPlanOrganizationTask, GetRevisionRequests, GetTasksForAnnualPlan, UpdatePlanOrganizationTaskStatus } from '../../../../services/AnnualPlanService'
import { type BudgetSummary } from '../../../../models/BudgetSummary'
import { AnnualPlanTabs } from '../PlanningDetails'
import { PriorityOneActivityName } from '../../../../models/PriorityOneActivityName'
import { CARD_COLORS } from '../../../../utils/colors'
import { generatePath, useNavigate } from 'react-router-dom'
import { BudgetAccordion, type ISDSummary, type BudgetItem } from './BudgetAccordion'
import { AppRoutes } from '../../../Routes'
import { PriorityThreeActivityName } from '../../../../models/PriorityThreeActivityName'
import { AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { useAnnualPlanUserRole } from '../../../../hooks/use-annualPlanUserRole'
import { useAuth } from '../../../../hooks/use-auth'
import { Modal } from '../../../customComponents/Modal'
import { RevisionFeedbackModal } from './RevisionFeedbackModal'
import { RevisionRequestDisplay } from './RevisionRequestDisplay'
import { toast } from 'react-toastify'
import { type PlanRevisionRequest } from '../../../../models/PlanRevisionRequest'
import { LockedBanner } from '../LockedBanner'
import { Download } from '@mui/icons-material'
import generatePDF, { Margin } from 'react-to-pdf'
import { ImplementationDetailsTabs } from '../../implementation/ImplementationDetails'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { AnnualPlanStatus } from '../../../../models/AnnualPlanStatus'
import { BasicTableExpansionProvider } from '../priorityActivities/BasicTableExpansionContext'

interface TaskGroup {
  organizationName: string
  tasks: PlanOrganizationTask[]
}

enum ModalState {
  None = 'None',
  NotReadyToSubmit = 'NotReadyToSubmit',
  ConfirmReadyToSubmit = 'ConfirmReadyToSubmit',
  ConfirmRevertStatus = 'ConfirmRevertStatus',
  ConfirmAccept = 'ConfirmAccept',
  SetNeedsRevision = 'SetNeedsRevision',
  GeneratingPdf = 'GeneratingPdf'
}

interface Props {
  annualPlanId: number
  rcnId: number
  organizationName: string
  showComments?: boolean
  implementationPhase: boolean
  // TODO: Can this narrow the type down?
  changeTab: (tab: number) => void
  onUpdateStatus: () => void
}

const mapISDSummaries = <T extends {
  isdName: string
  isdImageKey?: string
  totalAmount: number
  totalUpdatedAmount: number
  totalSpentAmount?: number
  planOrganizationTask?: PlanOrganizationTask
  planOrganizationId?: number
  priorityId?: number
  priorityNumber?: number
  priorityActivities?: any[]
}>(
    summaries: T[],
    annualPlanId: number,
    rcnId: number,
    budgetItemsBuilder: (summary: T) => BudgetItem[]
  ): ISDSummary[] => {
  return summaries.map((summary) => {
    const budgetItems = budgetItemsBuilder(summary)

    return {
      isdName: summary.isdName,
      isdImageKey: summary.isdImageKey,
      totalAmount: summary.totalAmount,
      totalUpdatedAmount: summary.totalUpdatedAmount,
      totalSpentAmount: summary.totalSpentAmount ?? 0,
      planOrganizationTask: summary.planOrganizationTask,
      planOrganizationId: summary.planOrganizationId,
      priorityId: summary.priorityId,
      priorityNumber: summary.priorityNumber,
      priorityActivities: summary.priorityActivities,
      annualPlanId,
      rcnId,
      needsReview: budgetItems.some(item => item.needsReview),
      budgetItems
    }
  })
}

export const BudgetOverviewTab = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const { user } = useAuth()
  const { userRole } = useAnnualPlanUserRole(props.annualPlanId)

  const [annualPlanStatus, setAnnualPlanStatus] = useState<AnnualPlanStatus | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<PlanOrganizationTaskStatus>()
  const [statusToSet, setStatusToSet] = useState<PlanOrganizationTaskStatus>()
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null)
  const [budgetTaskId, setBudgetTaskId] = useState<number>()
  const [incompleteTasks, setIncompleteTasks] = useState<PlanOrganizationTask[]>([])
  const [modalState, setModalState] = useState<ModalState>(ModalState.None)
  const [revisionRequests, setRevisionRequests] = useState<PlanRevisionRequest[]>([])
  const [fullyExpanded, setFullyExpanded] = useState(false)
  const pdfRef = useRef<HTMLDivElement>(null)

  const forceExpandAll = fullyExpanded && modalState === ModalState.GeneratingPdf

  const readOnly = userRole !== AnnualPlanRole.PlanAdministrator && userRole !== AnnualPlanRole.PlanReviewer
  const statusReadOnly = readOnly || (userRole === AnnualPlanRole.PlanAdministrator && user != null && user.currentOrgId !== props.rcnId)
  const revisionsReadOnly = userRole !== AnnualPlanRole.PlanAdministrator && userRole !== AnnualPlanRole.PlanReviewer
  const canShowRevisionRequests = selectedStatus != null && [PlanOrganizationTaskStatus.NeedsRevision, PlanOrganizationTaskStatus.UpdatesNeedsRevision].includes(selectedStatus)
  const planName = props.implementationPhase ? 'Implementation' : 'Annual'
  const isTrackingPhase = annualPlanStatus === AnnualPlanStatus.ProgressTracking
  const trackingPhaseIfKnown = annualPlanStatus == null ? undefined : isTrackingPhase
  const trackedLabel = isTrackingPhase ? 'Tracking' : 'Updates'
  const trackedTotal = isTrackingPhase ? budgetSummary?.totalSpentBudgetAmount ?? 0 : budgetSummary?.totalUpdatedBudgetAmount ?? 0

  const revisionRequestsByTask = useMemo(() => {
    const getUnresolvedRequest = (taskName: PlanOrganizationTaskName): PlanRevisionRequest | undefined =>
      revisionRequests.find(rr => rr.taskName === taskName && !rr.resolved)

    return {
      allPriorities: getUnresolvedRequest(PlanOrganizationTaskName.AllPriorities),
      rcnOperations: getUnresolvedRequest(PlanOrganizationTaskName.RcnOperations),
      priorityOne: getUnresolvedRequest(PlanOrganizationTaskName.PriorityOne),
      priorityTwo: getUnresolvedRequest(PlanOrganizationTaskName.PriorityTwo),
      priorityThree: getUnresolvedRequest(PlanOrganizationTaskName.PriorityThree),
      priorityFour: getUnresolvedRequest(PlanOrganizationTaskName.PriorityFour)
    }
  }, [revisionRequests])

  const planAdminStatuses = (): Array<SelectOption<string>> => {
    const options: Array<SelectOption<string>> = []

    switch (selectedStatus) {
      case PlanOrganizationTaskStatus.UpdatesReady:
      case PlanOrganizationTaskStatus.Drafting:
      case PlanOrganizationTaskStatus.NeedsRevision:
      case PlanOrganizationTaskStatus.UpdatesNeedsRevision:
        options.push({ id: PlanOrganizationTaskStatus.ReadyToSubmit, name: PlanOrganizationTaskStatus.ReadyToSubmit })
        break
      case PlanOrganizationTaskStatus.ReadyToSubmit:
        if (revisionRequests.length > 0) {
          const revisionStatus = props.implementationPhase ? PlanOrganizationTaskStatus.UpdatesNeedsRevision : PlanOrganizationTaskStatus.NeedsRevision
          options.push({ id: revisionStatus, name: revisionStatus })
        } else {
          const draftingStatus = props.implementationPhase ? PlanOrganizationTaskStatus.UpdatesReady : PlanOrganizationTaskStatus.Drafting
          options.push({ id: draftingStatus, name: draftingStatus })
        }
        break
      default: break
    }

    return options
  }

  const planReviewerStatuses = (): Array<SelectOption<string>> => {
    const options: Array<SelectOption<string>> = []
    if (selectedStatus === PlanOrganizationTaskStatus.Submitted) {
      options.push({ id: PlanOrganizationTaskStatus.Accepted, name: PlanOrganizationTaskStatus.Accepted })
      options.push({ id: PlanOrganizationTaskStatus.NeedsRevision, name: PlanOrganizationTaskStatus.NeedsRevision })
    }
    if (selectedStatus === PlanOrganizationTaskStatus.UpdatesSubmitted) {
      options.push({ id: PlanOrganizationTaskStatus.TrackingInProgress, name: PlanOrganizationTaskStatus.TrackingInProgress })
      options.push({ id: PlanOrganizationTaskStatus.UpdatesNeedsRevision, name: PlanOrganizationTaskStatus.UpdatesNeedsRevision })
    }
    return options
  }

  let statusOptions: Array<SelectOption<string>> = []
  if (selectedStatus != null) statusOptions.push({ id: selectedStatus, name: selectedStatus })
  if (userRole === AnnualPlanRole.PlanAdministrator) {
    statusOptions = statusOptions.concat(planAdminStatuses())
  } else if (userRole === AnnualPlanRole.PlanReviewer) {
    statusOptions = statusOptions.concat(planReviewerStatuses())
  }

  const loadBudgetSummary = async (): Promise<void> => {
    const summary = await handleApiResponse(GetBudgetSummary(props.annualPlanId))
    if (summary != null) setBudgetSummary(summary)
  }

  const loadPlanOrganizationTask = async (): Promise<void> => {
    const task = await handleApiResponse(GetPlanOrganizationTask(props.annualPlanId, props.rcnId, PlanOrganizationTaskName.BudgetOverview))
    if (task != null) {
      setSelectedStatus(task.status)
      setBudgetTaskId(task.id)
    }
  }

  const loadIncompleteTasks = async (): Promise<void> => {
    const tasks = await handleApiResponse(GetTasksForAnnualPlan(props.annualPlanId))
    if (tasks == null) return
    const requiredTasksToCheck = tasks.filter(task => [
      PlanOrganizationTaskName.RcnOperations,
      PlanOrganizationTaskName.PriorityOne,
      PlanOrganizationTaskName.PriorityTwo,
      PlanOrganizationTaskName.PriorityThree,
      PlanOrganizationTaskName.PriorityFour
    ].includes(task.taskName as PlanOrganizationTaskName))
    const completedStatus = props.implementationPhase ? PlanOrganizationTaskStatus.UpdatesSubmitted : PlanOrganizationTaskStatus.Accepted
    const incompleteTasks = requiredTasksToCheck
      .filter(task => task.status !== completedStatus)
    setIncompleteTasks(incompleteTasks)
  }

  const loadRevisionRequests = async (): Promise<void> => {
    const requests = await handleApiResponse(GetRevisionRequests(props.annualPlanId))
    if (requests != null) setRevisionRequests(requests)
  }

  const loadAnnualPlanStatus = async (): Promise<void> => {
    const status = await handleApiResponse(GetAnnualPlanStatus(props.annualPlanId))
    setAnnualPlanStatus(status)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await Promise.all([
        loadBudgetSummary(),
        loadPlanOrganizationTask(),
        loadIncompleteTasks(),
        loadRevisionRequests(),
        loadAnnualPlanStatus()
      ])
    }

    if (userRole != null) {
      void fetchData()
    }
  }, [userRole])

  const handleStatusChange = async (event: SelectChangeEvent): Promise<void> => {
    const newStatus = event.target.value as PlanOrganizationTaskStatus

    if (newStatus === selectedStatus) {
      return
    }

    setStatusToSet(newStatus)

    if (newStatus === PlanOrganizationTaskStatus.ReadyToSubmit) {
      if ((selectedStatus === PlanOrganizationTaskStatus.NeedsRevision || selectedStatus === PlanOrganizationTaskStatus.UpdatesNeedsRevision) && revisionRequests.some(rr => !rr.resolved)) {
        toast.error('Please resolve all revision requests.')
        return
      }

      if (incompleteTasks.length > 0) {
        setModalState(ModalState.NotReadyToSubmit)
      } else {
        setModalState(ModalState.ConfirmReadyToSubmit)
      }

      return
    }

    if (selectedStatus === PlanOrganizationTaskStatus.ReadyToSubmit &&
        (newStatus === PlanOrganizationTaskStatus.Drafting || newStatus === PlanOrganizationTaskStatus.NeedsRevision || newStatus === PlanOrganizationTaskStatus.UpdatesNeedsRevision || newStatus === PlanOrganizationTaskStatus.UpdatesReady)
    ) {
      setModalState(ModalState.ConfirmRevertStatus)
      return
    }

    if (newStatus === PlanOrganizationTaskStatus.Accepted || newStatus === PlanOrganizationTaskStatus.TrackingInProgress) {
      setModalState(ModalState.ConfirmAccept)
      return
    }

    if ((selectedStatus === PlanOrganizationTaskStatus.Submitted && newStatus === PlanOrganizationTaskStatus.NeedsRevision) || (selectedStatus === PlanOrganizationTaskStatus.UpdatesSubmitted && newStatus === PlanOrganizationTaskStatus.UpdatesNeedsRevision)) {
      setModalState(ModalState.SetNeedsRevision)
      return
    }

    if (props.implementationPhase && selectedStatus === PlanOrganizationTaskStatus.UpdatesSubmitted && newStatus === PlanOrganizationTaskStatus.UpdatesNeedsRevision) {
      setModalState(ModalState.SetNeedsRevision)
    }
  }

  const confirmStatusChange = async (newStatus: PlanOrganizationTaskStatus): Promise<void> => {
    const { success, errors } = await UpdatePlanOrganizationTaskStatus(budgetTaskId ?? 0, newStatus, props.implementationPhase)
    setModalState(ModalState.None)

    if (!success) {
      toast.error(errors[0] ?? 'There was a problem updating the status. Please try again.')
      return
    }

    setSelectedStatus(newStatus)

    if (newStatus === PlanOrganizationTaskStatus.NeedsRevision || newStatus === PlanOrganizationTaskStatus.UpdatesNeedsRevision) {
      await loadRevisionRequests()
    }

    props.onUpdateStatus()

    if (newStatus === PlanOrganizationTaskStatus.ReadyToSubmit) {
      props.changeTab(ImplementationDetailsTabs.SubmitPlanUpdates)
    }
  }

  const viewDetails = (planOrgId: number | undefined, taskId: number | undefined, activity: string): (() => void) | null => {
    if (planOrgId == null || taskId == null) {
      return null
    }

    return () => { handleViewDetails(planOrgId, taskId, activity) }
  }

  const handleViewDetails = (planOrgId: number, taskId: number, activity: string): void => {
    const path = props.implementationPhase ? AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY : AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY
    const route = generatePath(
      path,
      {
        rcnId: props.rcnId.toString(),
        planId: props.annualPlanId.toString(),
        tab: String(AnnualPlanTabs.PriorityActivities),
        planOrgId: planOrgId.toString(),
        planOrgTaskId: taskId.toString()
      }
    )
    nav(route + '?activity=' + activity)
  }

  const handleDownloadPlan = async (): Promise<void> => {
    if ((pdfRef.current == null) || modalState === ModalState.GeneratingPdf) return

    const waitForExpansion = async (duration: number): Promise<void> => {
      await new Promise((resolve) => setTimeout(resolve, duration))
    }

    const toggleAccordions = (expand: boolean): void => {
      const accordions = pdfRef.current?.querySelectorAll('[data-testid^="accordion-"]')
      accordions?.forEach((accordion) => {
        const accordionElement = accordion as HTMLElement
        const summary = accordionElement.querySelector('.MuiAccordionSummary-root')
        const isExpanded = accordionElement.classList.contains('Mui-expanded')
        if ((summary != null) && ((expand && !isExpanded) || (!expand && isExpanded))) {
          (summary as HTMLButtonElement).click()
        }
      })
    }

    const toggleTableRows = (expand: boolean): void => {
      const tables = pdfRef.current?.querySelectorAll('.MuiTable-root')
      tables?.forEach((table) => {
        const tableCells = table.querySelectorAll('.MuiTableCell-root')
        tableCells.forEach((tableCell) => {
          const tableCellElement = tableCell as HTMLTableCellElement
          tableCellElement.style.whiteSpace = expand ? 'normal' : 'nowrap'
          tableCellElement.style.textOverflow = expand ? 'clip' : 'ellipsis'
        })
      })
    }

    const generatePDFForSection = async (section: HTMLElement, fileName: string): Promise<void> => {
      try {
        const pdf = await generatePDF(() => section, {
          method: 'build',
          page: { margin: Margin.MEDIUM },
          overrides: { canvas: { ignoreElements: (el) => el.hasAttribute('data-hide-in-pdf') } }
        })
        if (pdf != null) {
          pdf.save(fileName)
        } else {
          toast.error('Failed to generate PDF.')
        }
      } catch (error) {
        toast.error('An error occurred while generating the PDF.')
        console.error(error)
      }
    }

    const splitContentIntoSections = (content: HTMLElement, maxHeight: number): HTMLElement[] => {
      const sections: HTMLElement[] = []
      let currentSection = document.createElement('div')
      currentSection.style.visibility = 'hidden'
      document.body.appendChild(currentSection)

      const measureHeight = (element: HTMLElement): number => {
        const parent = element.parentElement
        let needsAppend = false
        if (parent === null) {
          currentSection.appendChild(element)
          needsAppend = true
        }
        const height = element.offsetHeight
        if (needsAppend) {
          currentSection.removeChild(element)
        }
        return height
      }

      const splitNode = (node: HTMLElement): HTMLElement[] => {
        const nodeSections: HTMLElement[] = []
        let tempSection = document.createElement('div')
        tempSection.style.visibility = 'hidden'
        document.body.appendChild(tempSection)

        Array.from(node.childNodes).forEach(child => {
          const clonedChild = child.cloneNode(true) as HTMLElement
          const childHeight = measureHeight(clonedChild)

          if (measureHeight(tempSection) + childHeight > maxHeight) {
            if (tempSection.children.length > 0) {
              nodeSections.push(tempSection)
            }
            tempSection = document.createElement('div')
            tempSection.style.visibility = 'hidden'
            document.body.appendChild(tempSection)
          }

          if (childHeight > maxHeight) {
            const subSections = splitNode(clonedChild)
            nodeSections.push(...subSections)
          } else {
            tempSection.appendChild(clonedChild)
          }
        })

        if (tempSection.children.length > 0) {
          nodeSections.push(tempSection)
        }

        return nodeSections
      }

      Array.from(content.childNodes).forEach(child => {
        const clonedChild = child.cloneNode(true) as HTMLElement
        const childHeight = measureHeight(clonedChild)

        if (measureHeight(currentSection) + childHeight > maxHeight) {
          if (currentSection.children.length > 0) {
            sections.push(currentSection)
          }
          currentSection = document.createElement('div')
          currentSection.style.visibility = 'hidden'
          document.body.appendChild(currentSection)
        }

        if (childHeight > maxHeight) {
          const subSections = splitNode(clonedChild)
          sections.push(...subSections)
        } else {
          currentSection.appendChild(clonedChild)
        }
      })

      if (currentSection.children.length > 0) {
        sections.push(currentSection)
      }

      sections.forEach(section => {
        section.style.visibility = 'visible'
        document.body.removeChild(section)
      })

      return sections
    }

    try {
      setModalState(ModalState.GeneratingPdf)
      toggleAccordions(true)
      toggleTableRows(true)
      await waitForExpansion(1000)

      const maxHeight = 10000 // Approximate max height for not exceeding 10 pages
      const content = pdfRef.current

      if (content.offsetHeight > maxHeight) {
        const sections = splitContentIntoSections(content, maxHeight)
        const originalChildren = Array.from(content.children)
        originalChildren.forEach(child => content.removeChild(child))

        for (let i = 0; i < sections.length; i++) {
          content.appendChild(sections[i])
          await generatePDFForSection(content, `AnnualPlan-Part${i + 1}of${sections.length}.pdf`)
          content.removeChild(sections[i])
        }

        originalChildren.forEach(child => content.appendChild(child))
      } else {
        await generatePDFForSection(content, 'AnnualPlan.pdf')
      }
    } finally {
      toggleTableRows(false)
      toggleAccordions(false)
      setModalState(ModalState.None)
    }
  }

  if (budgetSummary == null) {
    return <CircularProgress />
  }

  return <>
    <LockedBanner status={selectedStatus} isTrackingPhase={trackingPhaseIfKnown} />

    <BasicTableExpansionProvider forceExpandAll={forceExpandAll}>
      <Box ref={pdfRef}>
        {modalState === ModalState.GeneratingPdf && (
          <Typography variant='h2' mb='1em' textAlign='center'>
            RCN Planned Activities and Budgeted Costs
          </Typography>
        )}

        <Box
          data-testid='budgetOverview'
          sx={{ bgcolor: 'var(--grey-100)', p: '1em', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant='h2'>
              {props.implementationPhase && 'Updates | '}Budget Overview - {props.organizationName}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: '1em',
                justifyContent: 'right',
                alignContent: 'center'
              }}
            >
              {props.implementationPhase && (
                <FormControlLabel
                  data-hide-in-pdf
                  control={
                    <Checkbox
                      data-testid='downloadFullyExpandedCheckbox'
                      checked={fullyExpanded}
                      disabled={modalState === ModalState.GeneratingPdf}
                      onChange={(e) => { setFullyExpanded(e.target.checked) }}
                    />
                }
                  label='Download Fully Expanded'
                />
              )}

              <IconButton
                data-hide-in-pdf
                aria-label={`Download ${planName} Plan PDF`}
                title={`Download ${planName} Plan PDF`}
                disabled={modalState === ModalState.GeneratingPdf}
                onClick={handleDownloadPlan}
              >
                {modalState === ModalState.GeneratingPdf && <CircularProgress size={10} />}
                <Download />
              </IconButton>

              {modalState !== ModalState.GeneratingPdf && (
                <>
                  <FormSelect
                    readOnly={statusReadOnly}
                    formControlProps={{ sx: { minWidth: '200px' } }}
                    name='planStatusSelect'
                    value={selectedStatus ?? ''}
                    options={statusOptions}
                    onChange={handleStatusChange}
                    hideLabel={true}
                  />
                  <Comments
                    parentType={CommentParentType.PlanOrganizationTask}
                    parentId={budgetTaskId ?? 0}
                    showComments={props.showComments}
                  />
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: '1em' }}>
            <Typography variant='h3'>
              Total Budget Request
            </Typography>

            {props.implementationPhase
              ? (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant='h2' sx={{ color: 'var(--blue-700)' }} data-testid='totalBudgetAmount'>
                    Contract: {budgetSummary.totalBudgetAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </Typography>
                  <Typography variant='h3' sx={{ color: 'var(--blue-500)' }} data-testid='totalUpdatedBudgetAmount'>
                    {trackedLabel}: {trackedTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </Typography>
                </Box>
                )
              : (
                <Typography variant='h2' sx={{ color: 'var(--blue-700)' }} data-testid='totalBudgetAmount'>
                  {budgetSummary.totalBudgetAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
                )
          }
          </Box>

          {revisionRequestsByTask.allPriorities != null && canShowRevisionRequests &&
            <Box sx={{ pt: '16px' }} data-testid='revisionRequest-allPriorities'>
              <RevisionRequestDisplay
                revisionRequest={revisionRequestsByTask.allPriorities}
                readOnly={revisionsReadOnly}
                onSave={loadRevisionRequests}
              />
            </Box>
      }
        </Box>

        <BudgetAccordion
          hideButtons={modalState === ModalState.GeneratingPdf}
          priorityName='RCN Operations'
          totalAmount={budgetSummary.rcnOperationsSummary.totalAmount}
          totalUpdatedAmount={budgetSummary.rcnOperationsSummary.totalUpdatedAmount}
          totalSpentAmount={budgetSummary.rcnOperationsSummary.totalSpentAmount ?? 0}
          dataTestId='expandRcnOperations'
          userRole={userRole}
          revisionRequest={canShowRevisionRequests ? revisionRequestsByTask.rcnOperations : undefined}
          readOnly={revisionsReadOnly}
          meetingTaskId={budgetSummary.meetingTaskId}
          onResolveRevisionRequest={loadRevisionRequests}
          isdSummaries={[]}
          rcnId={props.rcnId}
          annualPlanId={props.annualPlanId}
          implementationPhase={props.implementationPhase}
          isTrackingPhase={isTrackingPhase}
          topLevelBudgetItems={[
            {
              name: 'Meetings & RCN Support Costs',
              amount: budgetSummary.rcnOperationsSummary.totalAmount,
              updatedAmount: budgetSummary.rcnOperationsSummary.totalUpdatedAmount,
              spentAmount: budgetSummary.rcnOperationsSummary.totalSpentAmount ?? 0,
              needsReview: budgetSummary.rcnOperationsSummary.needsReview,
              viewDetails: () => { props.changeTab(AnnualPlanTabs.RcnOperations) },
              color: CARD_COLORS.dark[1],
              annualPlanId: props.annualPlanId,
              rcnId: props.rcnId
            }
          ]}
        />

        <BudgetAccordion
          hideButtons={modalState === ModalState.GeneratingPdf}
          priorityName='Priority 1: Professional Learning'
          totalAmount={budgetSummary.priorityOneSummary.totalAmount}
          totalUpdatedAmount={budgetSummary.priorityOneSummary.totalUpdatedAmount}
          totalSpentAmount={budgetSummary.priorityOneSummary.totalSpentAmount ?? 0}
          dataTestId='expandPriorityOne'
          userRole={userRole}
          revisionRequest={canShowRevisionRequests ? revisionRequestsByTask.priorityOne : undefined}
          readOnly={revisionsReadOnly}
          onResolveRevisionRequest={loadRevisionRequests}
          implementationPhase={props.implementationPhase}
          isTrackingPhase={isTrackingPhase}
          isdSummaries={mapISDSummaries(
            budgetSummary.priorityOneSummary.isdSummaries,
            props.annualPlanId,
            props.rcnId,
            (summary) => [
              {
                name: PriorityOneActivityName.RcnProvidedProfessionalDevelopment,
                amount: summary.professionalDevelopment.amount,
                updatedAmount: summary.professionalDevelopment.updatedAmount,
                spentAmount: summary.professionalDevelopment.spentAmount ?? 0,
                needsReview: summary.professionalDevelopment.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, PriorityOneActivityName.RcnProvidedProfessionalDevelopment),
                color: CARD_COLORS.dark[0]
              },
              {
                name: PriorityOneActivityName.StartEventAttendancePlan,
                amount: summary.eventAttendance.amount,
                updatedAmount: summary.eventAttendance.updatedAmount,
                spentAmount: summary.eventAttendance.spentAmount ?? 0,
                needsReview: summary.eventAttendance.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, PriorityOneActivityName.StartEventAttendancePlan),
                color: CARD_COLORS.dark[0]
              },
              {
                name: PriorityOneActivityName.EpliPlan,
                amount: summary.epliTrainer.amount,
                updatedAmount: summary.epliTrainer.updatedAmount,
                spentAmount: summary.epliTrainer.spentAmount ?? 0,
                needsReview: summary.epliTrainer.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, PriorityOneActivityName.EpliPlan),
                color: CARD_COLORS.dark[0]
              },
              {
                name: PriorityOneActivityName.OtherProfessionalDevelopmentResources,
                amount: summary.otherResources.amount,
                updatedAmount: summary.otherResources.updatedAmount,
                spentAmount: summary.otherResources.spentAmount ?? 0,
                needsReview: summary.otherResources.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, PriorityOneActivityName.OtherProfessionalDevelopmentResources),
                color: CARD_COLORS.dark[0]
              }
            ]
          )}
        />

        <BudgetAccordion
          hideButtons={modalState === ModalState.GeneratingPdf}
          priorityName='Priority 2: Coaching Structure and Systems'
          totalAmount={budgetSummary.priorityTwoSummary.totalAmount}
          totalUpdatedAmount={budgetSummary.priorityTwoSummary.totalUpdatedAmount}
          totalSpentAmount={budgetSummary.priorityTwoSummary.totalSpentAmount ?? 0}
          dataTestId='expandPriorityTwo'
          userRole={userRole}
          revisionRequest={canShowRevisionRequests ? revisionRequestsByTask.priorityTwo : undefined}
          readOnly={revisionsReadOnly}
          onResolveRevisionRequest={loadRevisionRequests}
          implementationPhase={props.implementationPhase}
          isTrackingPhase={isTrackingPhase}
          isdSummaries={mapISDSummaries(
            budgetSummary.priorityTwoSummary.isdSummaries,
            props.annualPlanId,
            props.rcnId,
            (summary) => [
              {
                name: 'Coaching Structure and Systems',
                amount: summary.coachingStructure.amount,
                updatedAmount: summary.coachingStructure.updatedAmount,
                spentAmount: summary.coachingStructure.spentAmount ?? 0,
                needsReview: summary.coachingStructure.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, 'Coaching Structure and Systems'),
                color: CARD_COLORS.dark[1]
              }
            ]
          )}
        />

        <BudgetAccordion
          hideButtons={modalState === ModalState.GeneratingPdf}
          priorityName='Priority 3: Coaching for Implementation of Evidence-based Practices'
          totalAmount={budgetSummary.priorityThreeSummary.totalAmount}
          totalUpdatedAmount={budgetSummary.priorityThreeSummary.totalUpdatedAmount}
          totalSpentAmount={budgetSummary.priorityThreeSummary.totalSpentAmount ?? 0}
          dataTestId='expandPriorityThree'
          userRole={userRole}
          revisionRequest={canShowRevisionRequests ? revisionRequestsByTask.priorityThree : undefined}
          readOnly={revisionsReadOnly}
          onResolveRevisionRequest={loadRevisionRequests}
          implementationPhase={props.implementationPhase}
          isTrackingPhase={isTrackingPhase}
          isdSummaries={mapISDSummaries(
            budgetSummary.priorityThreeSummary.isdSummaries,
            props.annualPlanId,
            props.rcnId,
            (summary) => [
              {
                name: PriorityThreeActivityName.CetaClassroomsAndFocusStudents,
                amount: summary.classroomsAndFocusStudents.amount,
                updatedAmount: summary.classroomsAndFocusStudents.updatedAmount,
                spentAmount: summary.classroomsAndFocusStudents.spentAmount ?? 0,
                needsReview: summary.classroomsAndFocusStudents.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, PriorityThreeActivityName.CetaClassroomsAndFocusStudents),
                color: CARD_COLORS.dark[2]
              }
            ]
          )}
        />

        <BudgetAccordion
          hideButtons={modalState === ModalState.GeneratingPdf}
          priorityName='Priority 4: Peer to Peer Programs'
          totalAmount={budgetSummary.priorityFourSummary.totalAmount}
          totalUpdatedAmount={budgetSummary.priorityFourSummary.totalUpdatedAmount}
          totalSpentAmount={budgetSummary.priorityFourSummary.totalSpentAmount ?? 0}
          dataTestId='expandPriorityFour'
          userRole={userRole}
          revisionRequest={canShowRevisionRequests ? revisionRequestsByTask.priorityFour : undefined}
          readOnly={revisionsReadOnly}
          onResolveRevisionRequest={loadRevisionRequests}
          sx={{ ':last-child': { borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', boxShadow: '0px 2px 0px -1px rgba(0, 0, 0, 0.2)' } }}
          implementationPhase={props.implementationPhase}
          isTrackingPhase={isTrackingPhase}
          isdSummaries={mapISDSummaries(
            budgetSummary.priorityFourSummary.isdSummaries,
            props.annualPlanId,
            props.rcnId,
            (summary) => [
              {
                name: 'Focus Buildings',
                amount: summary.focusBuildings.amount,
                updatedAmount: summary.focusBuildings.updatedAmount,
                spentAmount: summary.focusBuildings.spentAmount ?? 0,
                needsReview: summary.focusBuildings.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, 'Focus Buildings'),
                color: CARD_COLORS.dark[3]
              },
              {
                name: 'ISD/District Program Enhancements',
                amount: summary.programEnhancements.amount,
                updatedAmount: summary.programEnhancements.updatedAmount,
                spentAmount: summary.programEnhancements.spentAmount ?? 0,
                needsReview: summary.programEnhancements.needsReview,
                viewDetails: viewDetails(summary.planOrganizationId, summary.planOrganizationTask?.id, 'ISD/District Program Enhancements'),
                color: CARD_COLORS.dark[3]
              }
            ]
          )}
        />
      </Box>
    </BasicTableExpansionProvider>

    {modalState === ModalState.ConfirmReadyToSubmit &&
      <Modal
        open={true}
        title='Confirm Ready to Submit'
        cancelButtonText='No, I am not ready'
        confirmButtonText='Yes'
        onClose={() => { setModalState(ModalState.None) }}
        onConfirm={async () => { await confirmStatusChange(PlanOrganizationTaskStatus.ReadyToSubmit) }}
        bodyContent={`This action will lock your ${planName.toLowerCase()} plan and bring you to the "Submit ${planName} Plan" page. Are you ready to lock your ${planName.toLowerCase()} plan and proceed to the "Submit ${planName} Plan" page to finalize and submit your plan?`}
      />
    }

    {modalState === ModalState.ConfirmRevertStatus &&
      <Modal
        open={true}
        title={`Confirm ${planName} Plan Unlock`}
        cancelButtonText='No, do not revert'
        confirmButtonText='Yes, continue'
        onClose={() => { setModalState(ModalState.None) }}
        onConfirm={async () => { await confirmStatusChange(statusToSet ?? PlanOrganizationTaskStatus.Drafting) }}
        bodyContent={`This action will unlock your ${planName.toLowerCase()} plan. Are you sure you want to revert the status?`}
      />
    }

    {modalState === ModalState.ConfirmAccept &&
      <Modal
        open={true}
        title='Confirm Accept'
        cancelButtonText='No, I am not ready'
        confirmButtonText={`Yes, accept the ${planName.toLowerCase()} plan`}
        onClose={() => { setModalState(ModalState.None) }}
        onConfirm={async () => { await confirmStatusChange(statusToSet ?? (props.implementationPhase ? PlanOrganizationTaskStatus.TrackingInProgress : PlanOrganizationTaskStatus.Accepted)) }}
        bodyContent={`By accepting the budget for this ${planName.toLowerCase()} plan, you are accepting the ${planName.toLowerCase()} plan and it will no longer be able to be modified. Do you wish to proceed?`}
      />
    }

    {modalState === ModalState.NotReadyToSubmit &&
      <Modal
        open={true}
        title={`${props.implementationPhase ? 'Unsubmitted' : 'Unaccepted'} Activities`}
        hideConfirmButton={true}
        cancelButtonText='Close'
        onClose={() => { setModalState(ModalState.None) }}
        onConfirm={() => { }}
        bodyContent={
          <>
            <Typography variant='body1'>
              Your {planName} Plan is not ready to submit. The following priority activities are not {props.implementationPhase ? 'submitted' : 'accepted'}:
            </Typography>

            {/* Display tasks grouped by organization name */}
            {incompleteTasks.reduce((groups: TaskGroup[], task) => {
              const orgName = task.organizationName
              const groupIndex = groups.findIndex((group) => group.organizationName === orgName)
              if (groupIndex === -1) {
                groups.push({ organizationName: orgName, tasks: [task] })
              } else {
                groups[groupIndex].tasks.push(task)
              }
              return groups
            }, []).map((taskGroup, index) => (
              <Box key={index}>
                <Typography variant='h4'>{taskGroup.organizationName}</Typography>
                <ul style={{ marginTop: 0 }}>
                  {taskGroup.tasks.map((task) => (
                    <li key={task.id}>{task.taskName}</li>
                  ))}
                </ul>
              </Box>
            ))}
          </>
        }
      />
    }

    {modalState === ModalState.GeneratingPdf && (
      <Modal
        data-hide-in-pdf
        open={true}
        title='Generating PDF'
        onConfirm={() => {}}
        onClose={() => {}}
        hideCancelButton={true}
        hideXtoClose={true}
        hideConfirmButton={true}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant='body1' mt={2}>
              Generating PDF, please wait...
            </Typography>
          </Box>
        }
      />
    )}

    {modalState === ModalState.SetNeedsRevision &&
      <RevisionFeedbackModal
        planId={props.annualPlanId}
        onClose={() => { setModalState(ModalState.None) }}
        onConfirm={async () => {
          await confirmStatusChange(
            props.implementationPhase
              ? PlanOrganizationTaskStatus.UpdatesNeedsRevision
              : PlanOrganizationTaskStatus.NeedsRevision
          )
        }}
      />
    }
  </>
}
